import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import './Header.css';

const Header = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const navRef = useRef(null);
  const toggleBtnRef = useRef(null);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close user dropdown if clicked outside
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      // Close mobile menu if clicked outside both nav and toggle button
      if (
        navRef.current &&
        !navRef.current.contains(event.target) &&
        toggleBtnRef.current &&
        !toggleBtnRef.current.contains(event.target)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowUserMenu(false);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Close menus when navigating to another route
  useEffect(() => {
    setShowUserMenu(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    setIsMobileMenuOpen(false);
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => !prev);
    setShowUserMenu(false);
  };

  const toggleUserMenu = () => {
    setShowUserMenu((prev) => !prev);
    setIsMobileMenuOpen(false);
  };

  const getDashboardLink = () => {
    const role = user?.role;
    switch (role) {
      case 'super_admin':
      case 'admin':
      case 'subadmin':
        return '/admin/dashboard';
      case 'employer':
        return '/employer/dashboard';
      case 'consultant':
        return '/consultant/dashboard';
      case 'candidate':
        return '/candidate/dashboard';
      default:
        return '/';
    }
  };

  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <Link to="/" className="logo" onClick={() => setIsMobileMenuOpen(false)}>
            <img src="/images/logo.png" alt="HiringOpen" className="logo-img" />
          </Link>

          <nav
            ref={navRef}
            className={`nav-menu ${isMobileMenuOpen ? 'open' : ''}`}
            aria-label="Main navigation"
          >
            <Link to="/" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
            <Link to="/about" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>About</Link>
            <Link to="/blog" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Blog</Link>
            <Link to="/contact" className="nav-link" onClick={() => setIsMobileMenuOpen(false)}>Contact</Link>
          </nav>

          <div className="header-actions">
            {isAuthenticated ? (
              <div className="user-menu-container" ref={menuRef}>
                <button
                  className="user-menu-trigger"
                  onClick={toggleUserMenu}
                  aria-expanded={showUserMenu}
                  aria-label="User account menu"
                >
                  <span className="user-avatar-header">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                  <span className="user-name">Hi, {user?.name}</span>
                  <span className="dropdown-icon">{showUserMenu ? '▲' : '▼'}</span>
                </button>

                {showUserMenu && (
                  <div className="user-dropdown-menu">
                    <div className="dropdown-user-header">
                      <div className="user-avatar-large">
                        {user?.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="dropdown-user-name">{user?.name}</div>
                        <div className="dropdown-user-email">{user?.email}</div>
                      </div>
                    </div>
                    <div className="dropdown-divider"></div>
                    <div className="dropdown-menu-items">
                      <Link to={getDashboardLink()} className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                        <span className="dropdown-icon">
                          <span className="material-symbols-outlined">dashboard</span>
                        </span>
                        <span>Dashboard</span>
                      </Link>
                      {user?.role === 'candidate' && (
                        <>
                          <Link to="/candidate/profile" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                            <span className="dropdown-icon">
                              <span className="material-symbols-outlined">person</span>
                            </span>
                            <span>My Profile</span>
                          </Link>
                          <Link to="/candidate/my-applications" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                            <span className="dropdown-icon">
                              <span className="material-symbols-outlined">description</span>
                            </span>
                            <span>My Applications</span>
                          </Link>
                          <Link to="/candidate/saved-jobs" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                            <span className="dropdown-icon">
                              <span className="material-symbols-outlined">bookmark</span>
                            </span>
                            <span>Saved Jobs</span>
                          </Link>
                        </>
                      )}
                      {user?.role === 'employer' && (
                        <>
                          <Link to="/employer/my-jobs" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                            <span className="dropdown-icon">
                              <span className="material-symbols-outlined">work</span>
                            </span>
                            <span>My Jobs</span>
                          </Link>
                          <Link to="/employer/applications" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                            <span className="dropdown-icon">
                              <span className="material-symbols-outlined">assignment</span>
                            </span>
                            <span>Applications</span>
                          </Link>
                          <Link to="/employer/profile" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                            <span className="dropdown-icon">
                              <span className="material-symbols-outlined">business</span>
                            </span>
                            <span>Company Profile</span>
                          </Link>
                        </>
                      )}
                      {user?.role === 'consultant' && (
                        <>
                          <Link to="/consultant/companies" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                            <span className="dropdown-icon">
                              <span className="material-symbols-outlined">business</span>
                            </span>
                            <span>Companies</span>
                          </Link>
                          <Link to="/consultant/jobs" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                            <span className="dropdown-icon">
                              <span className="material-symbols-outlined">work</span>
                            </span>
                            <span>My Jobs</span>
                          </Link>
                          <Link to="/consultant/applications" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                            <span className="dropdown-icon">
                              <span className="material-symbols-outlined">assignment</span>
                            </span>
                            <span>Applications</span>
                          </Link>
                        </>
                      )}
                      {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'subadmin') && (
                        <>
                          <Link to="/admin/users" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                            <span className="dropdown-icon">
                              <span className="material-symbols-outlined">groups</span>
                            </span>
                            <span>Manage Users</span>
                          </Link>
                          <Link to="/admin/companies" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                            <span className="dropdown-icon">
                              <span className="material-symbols-outlined">business</span>
                            </span>
                            <span>Companies</span>
                          </Link>
                        </>
                      )}
                    </div>
                    <div className="dropdown-divider"></div>
                    <button onClick={handleLogout} className="dropdown-logout-btn">
                      <span className="dropdown-icon">
                        <span className="material-symbols-outlined">logout</span>
                      </span>
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" className="btn btn-theme-green btn-sm">
                  Sign In
                </Link>
              </>
            )}

            <button
              ref={toggleBtnRef}
              type="button"
              className={`mobile-menu-toggle ${isMobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
              aria-expanded={isMobileMenuOpen}
            >
              <span className="material-symbols-outlined">
                {isMobileMenuOpen ? 'close' : 'menu'}
              </span>
            </button>
          </div>

        </div>
      </div>
    </header>
  );
};

export default Header;
